import type { Point } from "./types.ts";

/**
 * The point exactly halfway along the link's own full path (from ->
 * vertices -> to, by total length, not just the count of points), and the
 * angle of whichever segment contains that point, so a bent conduit's
 * arrow follows its actual local direction there.
 *
 * Deliberately returns the angle in the SAME Y-axis convention as whatever
 * `points` are already in — a caller working in a Y-up plane (unlike SVG's
 * Y-down) must negate the result before using it as a CSS `rotate()`.
 */
export function pathMidpointAndAngle(points: Point[]): { point: Point; angleDeg: number } | null {
  if (points.length < 2) return null;

  let totalLength = 0;
  const segments = points.slice(0, -1).map((a, i) => {
    const b = points[i + 1];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const length = Math.hypot(dx, dy);
    totalLength += length;
    return { a, dx, dy, length };
  });
  if (totalLength === 0) return null;

  let remaining = totalLength / 2;
  for (const seg of segments) {
    if (remaining <= seg.length || seg === segments[segments.length - 1]) {
      const t = seg.length === 0 ? 0 : Math.min(remaining / seg.length, 1);
      return {
        point: { x: seg.a.x + seg.dx * t, y: seg.a.y + seg.dy * t },
        angleDeg: (Math.atan2(seg.dy, seg.dx) * 180) / Math.PI,
      };
    }
    remaining -= seg.length;
  }
  return null;
}
